import { useState } from "react";
import { Link } from "react-router-dom";
import { Box, Button, Chip, Container, Typography } from "@mui/material";
import { useBlogList, useBlogCategories, subscribeToNewsletter } from "../../../hooks/useBlog";
import { colors, fontFamilies } from "../../../constants/theme";
import SmartImage from "../../../components/SmartImage";

const CARD_BG = "#ffffff";
const CARD_RADIUS = "10px";
const IMG_RADIUS_FEATURED = "30px";
const IMG_RADIUS = "16px";
const CARD_SHADOW = "0 6px 24px rgba(51, 38, 29, .06)";
const PILL_BG = "#F0ECE4";

const fmtDate = (d) => {
  if (!d) return "";
  const parsed = new Date(d);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const MetaRow = ({ post }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5, flexWrap: "wrap" }}>
    {post.category && (
      <Chip
        label={post.category}
        sx={{
          bgcolor: PILL_BG, color: colors.primary, fontWeight: 600,
          fontFamily: fontFamilies.gotham, fontSize: 13, letterSpacing: 0.2, height: 34,
          px: 0.5, borderRadius: "33px",
        }}
      />
    )}
    {fmtDate(post.date) && (
      <Typography sx={{ fontFamily: fontFamilies.quicksand, color: colors.textLight, fontSize: 13 }}>
        {fmtDate(post.date)}
      </Typography>
    )}
  </Box>
);

const Card = ({ post, featured }) => (
  <Box
    component={Link}
    to={`/blog/${post.slug}`}
    sx={{
      display: "flex",
      flexDirection: featured ? { xs: "column", md: "row" } : "column",
      textDecoration: "none", color: "inherit",
      bgcolor: CARD_BG, borderRadius: CARD_RADIUS, boxShadow: CARD_SHADOW,
      p: featured ? { xs: 1.5, md: 2 } : 1.5, height: "100%",
      transition: "box-shadow .2s, transform .2s",
      "&:hover": { boxShadow: "0 12px 32px rgba(51, 38, 29, .12)", transform: "translateY(-2px)" },
    }}
  >
    {post.coverImage && (
      <SmartImage
        src={post.coverImage}
        alt={post.title}
        height={featured ? { xs: 220, md: 360 } : 200}
        radius={featured ? IMG_RADIUS_FEATURED : IMG_RADIUS}
        sx={{ width: featured ? { xs: "100%", md: "46%" } : "100%" }}
      />
    )}
    <Box
      sx={{
        p: "24px", flex: 1,
        display: "flex", flexDirection: "column",
        justifyContent: featured ? "center" : "flex-start",
      }}
    >
      <MetaRow post={post} />
      <Typography
        sx={{
          fontFamily: fontFamilies.gotham, fontWeight: 400, color: colors.primary,
          fontSize: featured ? { xs: 24, md: 32 } : 20, lineHeight: 1.25, mb: 1.5,
        }}
      >
        {post.title}
      </Typography>
      <Typography
        sx={{
          fontFamily: fontFamilies.quicksand, color: colors.textLight,
          fontSize: featured ? 16 : 14, lineHeight: 1.6, mb: 3,
          display: "-webkit-box", WebkitLineClamp: featured ? 4 : 4, WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {post.excerpt}
      </Typography>
      <Box sx={{ mt: "auto" }}>
        {featured ? (
          <Button
            variant="contained" disableElevation
            sx={{
              bgcolor: colors.secondary, color: "#fff", textTransform: "none",
              fontFamily: fontFamilies.quicksand, fontWeight: 600, px: 3, py: 1.25,
              borderRadius: "10px", "&:hover": { bgcolor: "#22344a" },
            }}
          >
            Read Article
          </Button>
        ) : (
          <Button
            variant="outlined"
            sx={{
              color: colors.secondary, borderColor: colors.secondary, textTransform: "none",
              fontFamily: fontFamilies.quicksand, fontWeight: 600, px: 3, py: 0.75,
              borderRadius: "10px", "&:hover": { borderColor: colors.secondary, bgcolor: "rgba(41,64,90,.04)" },
            }}
          >
            Read More
          </Button>
        )}
      </Box>
    </Box>
  </Box>
);

const Blog = () => {
  const [category, setCategory] = useState("All");
  const [page, setPage] = useState(1);
  const [accum, setAccum] = useState([]);
  const categories = useBlogCategories(); // array of strings
  const { items, totalPages, loading } = useBlogList({ page, category });

  // Newsletter sign-up: the form used to swallow the submit, so nothing left the
  // page at all (QA 2026-07-28).
  const [subEmail, setSubEmail] = useState("");
  const [subState, setSubState] = useState({ busy: false, message: "", error: false });

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (subState.busy) return;
    const email = subEmail.trim();
    if (!email) {
      setSubState({ busy: false, message: "Please enter your email address.", error: true });
      return;
    }
    setSubState({ busy: true, message: "", error: false });
    try {
      const data = await subscribeToNewsletter(email);
      setSubState({ busy: false, message: data?.message || "Thanks — you are subscribed.", error: false });
      setSubEmail("");
    } catch (err) {
      setSubState({
        busy: false,
        message: err.response?.data?.message || "Something went wrong. Please try again.",
        error: true,
      });
    }
  };

  const onCategory = (c) => { setCategory(c); setPage(1); setAccum([]); };
  const list = page === 1 ? items : [...accum, ...items];
  const [featured, ...rest] = list;
  const chips = ["All", ...categories];

  return (
    <Box sx={{ py: { xs: 6, md: 10 } }}>
      <Container maxWidth="lg">
        <Typography
          sx={{
            textAlign: "center", fontFamily: fontFamilies.gotham, color: colors.primary,
            fontSize: 13, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", mb: 1.5,
          }}
        >
          Perspectives &amp; Insights
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontFamily: fontFamilies.gotham, fontWeight: 400, textAlign: "center", mb: 2,
            color: colors.primary, fontSize: { xs: 32, md: 46 },
          }}
        >
          The Nirva Blog
        </Typography>
        <Typography
          sx={{
            textAlign: "center", fontFamily: fontFamilies.quicksand, color: colors.textLight,
            mb: 5, maxWidth: 520, mx: "auto", fontSize: 16,
          }}
        >
          Thoughtful explorations of hypnotherapy, interpersonal healing, and the quiet work of becoming more yourself.
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "center", mb: 6 }}>
          {chips.map((c) => {
            const active = c === category;
            return (
              <Chip
                key={c} label={c} onClick={() => onCategory(c)}
                sx={{
                  px: 2, height: 44, fontWeight: 600, borderRadius: "10px", cursor: "pointer",
                  fontFamily: fontFamilies.quicksand, fontSize: 14,
                  bgcolor: active ? colors.secondary : "transparent",
                  color: active ? "#fff" : colors.secondary,
                  border: `1px solid ${colors.secondary}`,
                  "&:hover": { bgcolor: active ? "#22344a" : "rgba(41,64,90,.06)" },
                }}
              />
            );
          })}
        </Box>

        {loading && list.length === 0 ? (
          <Typography sx={{ textAlign: "center", fontFamily: fontFamilies.quicksand, color: colors.textLight }}>
            Loading…
          </Typography>
        ) : list.length === 0 ? (
          <Typography sx={{ textAlign: "center", fontFamily: fontFamilies.quicksand, color: colors.textLight }}>
            No posts yet. Check back soon.
          </Typography>
        ) : (
          <>
            {featured && <Box sx={{ mb: 4 }}><Card post={featured} featured /></Box>}
            {rest.length > 0 && (
              <Box sx={{
                display: "grid", gap: 3,
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
              }}>
                {rest.map((p) => <Card key={p.slug} post={p} />)}
              </Box>
            )}
            {page < totalPages && (
              <Box sx={{ textAlign: "center", mt: 6 }}>
                <Button
                  variant="outlined" disabled={loading}
                  onClick={() => { setAccum(list); setPage((p) => p + 1); }}
                  sx={{
                    color: colors.secondary, borderColor: colors.secondary, textTransform: "none",
                    fontFamily: fontFamilies.quicksand, fontWeight: 600, px: 4, py: 1, borderRadius: 10,
                    "&:hover": { borderColor: colors.secondary, bgcolor: "rgba(41,64,90,.04)" },
                  }}
                >
                  {loading ? "Loading…" : "Load More Articles"}
                </Button>
              </Box>
            )}
          </>
        )}

        {/* Newsletter — posts to /api/blog/subscribe */}
        <Box sx={{ mt: 10, p: { xs: 3, md: 6 }, bgcolor: "#F1ECE3", borderRadius: 4, textAlign: "center" }}>
          <Typography
            sx={{
              fontFamily: fontFamilies.gotham, color: colors.textLight, fontSize: 12, fontWeight: 600,
              letterSpacing: 3, textTransform: "uppercase", mb: 1.5,
            }}
          >
            Stay Connected
          </Typography>
          <Typography
            sx={{ fontFamily: fontFamilies.gotham, fontWeight: 400, mb: 1, color: colors.primary, fontSize: { xs: 24, md: 30 } }}
          >
            Reflections, Delivered Gently
          </Typography>
          <Typography sx={{ fontFamily: fontFamilies.quicksand, color: colors.textLight, mb: 3, maxWidth: 440, mx: "auto" }}>
            Receive new articles, practitioner insights, and mindful resources.
          </Typography>
          <Box
            component="form" onSubmit={onSubscribe}
            sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}
          >
            <Box
              component="input" type="email" placeholder="Your email address"
              value={subEmail}
              onChange={(e) => setSubEmail(e.target.value)}
              disabled={subState.busy}
              sx={{
                px: 2, py: 1.25, borderRadius: 1.5, border: "1px solid #D8CFC4",
                minWidth: 260, fontSize: 15, outline: "none", fontFamily: fontFamilies.quicksand,
                bgcolor: "#fff",
              }}
            />
            <Button
              type="submit" variant="contained" disableElevation disabled={subState.busy}
              sx={{
                bgcolor: colors.secondary, px: 3, borderRadius: 1.5, textTransform: "none",
                fontFamily: fontFamilies.quicksand, fontWeight: 600, "&:hover": { bgcolor: "#22344a" },
              }}
            >
              {subState.busy ? "Subscribing…" : "Subscribe"}
            </Button>
          </Box>
          {subState.message && (
            <Typography
              sx={{
                mt: 2, fontFamily: fontFamilies.quicksand, fontSize: 14,
                color: subState.error ? "#B23A48" : colors.primary,
              }}
            >
              {subState.message}
            </Typography>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default Blog;
